import {
  forwardRef,
  ComponentPropsWithoutRef,
  ChangeEvent,
  ReactElement,
  useId,
} from "react";
import cx from "classnames";
import { Icon } from "../../Icon";
import Radio_outline from "@servicetitan/hammer-icon/mdi/two-tone/radio_button_unchecked.svg";
import Radio_checked from "@servicetitan/hammer-icon/mdi/two-tone/radio_button_checked.svg";
import styles from "../Radio.module.scss";
import { LayoutUtilProps, Svg } from "../../../types";
import { useLayoutPropsUtil } from "../../../internal/hooks";
import { Helper, HelperProps } from "../../../internal/components";
import { RadioState } from "../types";
import { useRadioGroupContext } from "./RadioGroupContext";

/**
 * Props for the Radio component
 * @extends Omit<ComponentPropsWithoutRef<"input">, "onChange">
 * @extends LayoutUtilProps
 */
export type RadioProps = Omit<ComponentPropsWithoutRef<"input">, "onChange"> &
  LayoutUtilProps & {
    /**
     * Controlled state
     * @default false
     */
    checked?: boolean;

    /**
     * Uncontrolled state
     * @default false
     */
    defaultChecked?: boolean;

    /**
     * Callback when radio is changed
     */
    onChange?: (e: ChangeEvent<HTMLInputElement>, state?: RadioState) => void;

    /**
     * Label for Radio
     * @remarks This should either be a string or have text content inside for accessibility
     */
    label?: ReactElement | string;

    /**
     * Error state for the radio
     * @default false
     */
    error?: boolean;

    /**
     * Icon overrides for checked and unchecked states
     */
    icons?: {
      /**
       * Icon to display when radio is checked
       */
      checked?: Svg;
      /**
       * Icon to display when radio is unchecked
       */
      unchecked?: Svg;
    };

    /**
     * Description text to display below the radio
     */
    description?: HelperProps["description"];

    /**
     * When true, hides the radio for visual users
     * @default false
     */
    hideRadio?: boolean;

    /**
     * Additional className applied to the label wrapper span
     */
    labelWrapperClassName?: string;
  };

const RadioElement = forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    checked,
    defaultChecked,
    value,
    icons,
    error,
    label,
    onChange,
    style,
    description,
    hideRadio = false,
    labelWrapperClassName,
    ...rest
  } = componentProps;

  const groupContext = useRadioGroupContext();

  const {
    checked: checkedIcon = Radio_checked,
    unchecked: uncheckedIcon = Radio_outline,
  } = { ...icons };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e, { value, checked: e.target.checked });
  };

  const helperUid = useId();

  // Combine local error state with group error state
  const hasError = error || groupContext?.hasError;

  const wrapperClassNames = cx(
    styles["wrapper"],
    { [styles["focus"]]: hideRadio },
    className,
  );

  const radioClassNames = cx(styles["radio"], {
    [styles["error"]]: hasError,
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <div className={wrapperClassNames} style={styleCombined} data-anv="radio">
      <label className={radioClassNames}>
        <input
          type="radio"
          aria-checked={checked ?? defaultChecked}
          checked={checked}
          defaultChecked={defaultChecked}
          value={value}
          onChange={onChangeHandler}
          aria-describedby={description ? helperUid : undefined}
          ref={ref}
          {...rest}
        />

        <span
          className={cx(styles["icon-wrapper"], {
            [styles["sr-only"]]: hideRadio,
          })}
        >
          <Icon
            size="large"
            svg={uncheckedIcon}
            className={cx([styles["icon-unchecked"]])}
          />

          <Icon
            size="large"
            svg={checkedIcon}
            className={cx([styles["icon-checked"]])}
          />
        </span>

        <span
          className={cx(labelWrapperClassName, {
            [styles["focus-label"]]: hideRadio,
          })}
        >
          {label}
        </span>
      </label>

      {description ? (
        <div className={styles["helper-wrapper"]}>
          <Helper id={helperUid} description={description} />
        </div>
      ) : null}
    </div>
  );
});

RadioElement.displayName = "RadioElement";

/**
 * Radio component for single selection from a group of options.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Customizable icons for checked and unchecked states
 * - Error state with visual indicators
 * - Description text support
 * - Accessibility support with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Optional radio hiding for visual users
 * - Flexible labeling options
 * - Group support via Radio.Group
 *
 * @example
 * <Radio
 *   label="Option 1"
 *   value="option1"
 *   checked={selectedValue === "option1"}
 *   onChange={(e, state) => setSelectedValue(state?.value)}
 * />
 *
 * @example
 * <Radio
 *   label="Custom styled option"
 *   value="custom"
 *   icons={{
 *     checked: CustomCheckedIcon,
 *     unchecked: CustomUncheckedIcon
 *   }}
 *   description="This option has custom styling"
 * />
 *
 * @example
 * <Radio.Group legend="Choose your preference" required>
 *   <Radio label="Option A" value="a" />
 *   <Radio label="Option B" value="b" />
 *   <Radio label="Option C" value="c" />
 * </Radio.Group>
 */
export const Radio = RadioElement;
